import {getIDType}             from '@/lib/utils/commonUtils';
import CryptoJS                from 'crypto-js';
import {buildXpathFromPayload} from '../xpath';
import {baseQuery}             from '@/lib/utils/searchUtils';
import {get}                   from 'lodash-es';
import {auth}                  from "@/auth"


/**
 * Generates a SHA-256 hash for the given input password.
 *
 * This function takes a plaintext password as input and produces a SHA-256
 * hash, which is then encoded into a Base64 string for storage or transmission.
 *
 * @param {string} password - The plaintext password to be hashed.
 * @return {string} A Base64 encoded string representing the SHA-256 hash of the input.
 */
export const generateSha256Hash = (password) => {
    const hashed = CryptoJS.SHA256(password);
    return CryptoJS.enc.Base64.stringify(hashed);
};

/**
 * Get the authorization header, if there is a logged-in user, use their header
 * @param {string} [username]
 * @param {string} [partyId]
 * @param {string} [password]
 */
export const getAuthorization = async (username = null, partyId = null, password = null) => {
    /**
     * @type {*}
     */
    let session = await auth();
    if (session?.user?.authorization) {
        return session.user.authorization
    }
    if (username && partyId && password) {
        return `Eidr ${username}:${partyId}:${generateSha256Hash(password)}`;
    }
    const hashedPassword = generateSha256Hash(process.env.EIDR_API_CREDS_PASSWORD);
    return `Eidr ${process.env.EIDR_API_CREDS_USERNAME}:${process.env.EIDR_API_CREDS_PARTYID}:${hashedPassword}`;
};


/**
 * @typedef {{ID: string, Full: string, DOIKernel: string, SelfDefined: string, Simple: string, Provenance: string, AlternateIDs: string, LinkedAlternateID: string}} SearchType
 */
export const SearchType = {
    ID: 'ID',
    Full: 'Full',
    DOIKernel: 'DOIKernel',
    SelfDefined: 'SelfDefined',
    Simple: 'Simple',
    Provenance: 'Provenance',
    AlternateIDs: 'AlternateIDs',
    LinkedAlternateID: 'LinkedAlternateID',
};

/**
 * Request a Single Record from the Resolve Proxy Service
 * @param {string} ID - the id of the record you are looking for
 * @param {string} searchType - the type of search to perform on the resolve service
 * @return {Promise<import('../types/common-types').RequestResponse>}
 */
export const resolve = async (ID, searchType = SearchType.Full) => {
    let start = new Date().getTime();

    console.log('resolve called with ID:', ID, 'and searchType:', searchType);
    let type = getIDType(ID);
    if (!type) {
        return {
            response: {
                error: {
                    message: 'Invalid ID'
                },
            },
            request: {}

        };
    }

    let url = process.env.EIDR_API_PROXY_URI + `/resolve/${ID}`;
    if (type === 'content') {
        url += `?type=${searchType}`;
    }

    try {

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                Authorization: await getAuthorization(),
            },
        });

        const text = await response.text();

        if (response.status > 299) {
            return {
                response: {
                    error: {
                        message: response.statusText,
                    },
                },
                request: {
                    url: url,
                    type: type,
                    headers: {'Content-Type': 'application/json'},
                },
            };
        }

        let results = JSON.parse(text);

        if (type === 'party' || type === 'service') {
            return {
                response: results,
                request: {
                    url: url,
                    type: type,
                    headers: {'Content-Type': 'application/json'},
                },
            };
        }

        /**
         * Post Search Options. To make a complete record, we may need to make some additional calls
         */
        if (searchType === SearchType.Full) {
            results.ProvenanceMetadata = await getProvenance(ID);

            if (results.ReferentType === 'Season') {
                results.Episodes = await getEpisodes(ID);
                results.Seasons = await getSeasons(
                    results.ExtraObjectMetadata.SeasonInfo.Parent,
                );
                results.Seasons = results.Seasons.filter((season) => season.ID !== ID);
            } else if (results.ReferentType === 'Series') {
                results.Seasons = await getSeasons(ID);
            } else if (
                results.ReferentType === 'TV' &&
                results?.ExtraObjectMetadata?.EpisodeInfo?.Parent
            ) {
                let {response, request} = await resolve(
                    results.ExtraObjectMetadata.EpisodeInfo.Parent,
                    SearchType.Full,
                );
                if (response) {
                    results.Parent = response;
                }
                results.Edits = await getEdits(ID);
                results.Clips = await getClips(ID);
            } else if (results.ReferentType === 'Episode') {
                results.Edits = await getEdits(ID);
                results.Clips = await getClips(ID);
            } else if (results.ReferentType === 'Movie') {
                results.Edits = await getEdits(ID);
                results.Clips = await getClips(ID);
            }
        } else {
            console.warn('Not getting full record', searchType);
        }


        return {
            response: results,
            request: {
                url: url,
                type: type,
                headers: {'Content-Type': 'application/json'},
                duration: (new Date().getTime() - start) / 1000,
            },
        };
    } catch (error) {
        console.log('Something Went Wrong', error);
        return {
            response: {error: error},
            request: {
                url: url,
                type: type,
                headers: {'Content-Type': 'application/json'},
                duration: (new Date().getTime() - start) / 1000,
            }
        };
    }
}

/**
 * Get the Provenance for a record
 * @param {string} ID
 */
export async function getProvenance(ID) {
    let url = process.env.EIDR_API_PROXY_URI + `/resolve/${ID}?type=Provenance`;
    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            Authorization: await getAuthorization(),
        },
    });
    if (response.status > 299) {
        return null;
    }
    const text = await response.text();
    return JSON.parse(text);
}

/**
 * Get the seasons for a record
 * @param {string} seriesId
 * @return <Promise IContentRecord[]>
 */
export async function getSeasons(seriesId) {
    let template = `(/FullMetadata/ExtraObjectMetadata/SeasonInfo/Parent "${seriesId}")`;
    let {response, request} = await searchByXpath(template);
    // @ts-ignore
    if (response.error) {
        return null;
    }
    // @ts-ignore
    return response.results;
}

/**
 * Get the Episodes for a record
 * @param {string} seasonId
 * @return <Promise IContentRecord[]>
 *  */
export async function getEpisodes(seasonId) {
    let template = `(/FullMetadata/ExtraObjectMetadata/EpisodeInfo/Parent "${seasonId}")`;
    let {response, request} = await searchByXpath(template);
    // @ts-ignore
    if (response.error) {
        return null;
    }
    // @ts-ignore
    return response.results;
}

/**
 * Get the edits for a record
 * @param {string} episodeId
 * @return <Promise IContentRecord[]>
 */
export async function getEdits(episodeId) {
    let template = `(/FullMetadata/ExtraObjectMetadata/EditInfo/Parent "${episodeId}")`;
    let {response, request} = await searchByXpath(template);
    console.log('getEdits', template, response);
    // @ts-ignore
    if (response.error) {
        return null;
    }
    // @ts-ignore
    return response.results;
}

/**
 * Get the clips for a Record
 * @param {string} episodeId
 * @return {Promise <IContentRecord[]>}
 */
export async function getClips(episodeId) {
    let template = `(/FullMetadata/ExtraObjectMetadata/ClipInfo/Parent "${episodeId}")`;
    let {response, request} = await searchByXpath(template);
    // @ts-ignore
    if (response.error) {
        return null;
    }
    // @ts-ignore
    return response.results;
}

/**
 * Executes a search query against the proxy API and retrieves the results based on the specified parameters.
 *
 * @param {array} ids - An array of ID's to search for
 * @param {string} [searchType=SearchType.Full] - The type of search to perform (e.g., full, partial).
 * @return {Promise<import('../types/common-types').RequestResponseArray>}
 **/
export async function searchById(ids, searchType = SearchType.Full) {
    let url = process.env.EIDR_API_PROXY_URI + `/resolve/?type=${searchType}`;
    let startTime = new Date().getTime();
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: await getAuthorization(),
        },
        body : JSON.stringify({ids: ids}),
    });
    if (response.status > 299) {
        return {
            response: {
                error: {
                    message: response.statusText,
                },
            },
            request: {
                url: url,
                headers: {'Content-Type': 'application/json'},
            }
        }
    }

    const responseData = await response.json();

    return {
        response: {results:responseData},
        request: {
            method: 'POST',
            url: url,
            duration: (new Date().getTime() - startTime) / 1000,
            headers: {'Content-Type': 'text/plain'},
        },
    };
}

/**
 * Executes a search query against the proxy API and retrieves the results based on the specified parameters.
 *
 * @param {object} query - The search query parameters including filters, pagination, and sorting options.
 * @param {string} [searchType=SearchType.Full] - The type of search to perform (e.g., full, partial).
 * @param {string} [idOnly='false'] - A flag to indicate if only IDs should be returned in the response.
 * @param {boolean} [idSearch] - If using the multiple ID resolution service {"ids":["id1", "id2", "id3"]}
 * @return {Promise<import('../types/common-types').RequestResponseArray>}
 **/
export async function search(
    query,
    searchType = SearchType.Full,
    idOnly = 'false',
    idSearch = false,
) {
    let startTime = new Date().getTime();
    let {pageSize, pageNumber, sort, order, ...searchFields} = query;

    pageSize = pageSize >= 0 ? pageSize : 500;
    pageNumber = pageNumber || 1;
    sort = sort || 'date'; //convertSortField(sort || 'ResourceName.value');
    order = order || 'asc';

    searchFields.pageSize = pageSize;
    searchFields.pageNumber = pageNumber;
    searchFields.sort = sort;
    searchFields.order = order;
    searchFields.type = searchType;
    searchFields.idOnly = idOnly;

    const xpath = buildXpathFromPayload(searchFields);

    let url = `${process.env.EIDR_API_PROXY_URI}/query?pageSize=${Math.min(pageSize, 500)}&pageNumber=${pageNumber || 1}&sort=${sort || 'title'}&order=${order || 'asc'}&type=${searchType}`;

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'text/plain',
                Authorization: await getAuthorization(),
            },
            body: xpath,
        });

        const text = await response.text();

        const responseData = JSON.parse(text);

        return {
            response: responseData,
            request: {
                method: 'POST',
                url: url,
                duration: (new Date().getTime() - startTime) / 1000,
                body: xpath,
                query: query,
                headers: {'Content-Type': 'text/plain'},
            },
        };
    } catch (error) {
        return {
            response: {error: error.message}, request: {
                method: 'POST',
                url: url,
                duration: (new Date().getTime() - startTime) / 1000,
                body: xpath,
                query: query,
                headers: {'Content-Type': 'text/plain'},
            }
        };
    }
}

/**
 * Searches for resources using the provided XPath query and search type. The method sends a POST request to a defined
 * API with necessary headers and query parameters. It returns the response data and the corresponding request details.
 *
 * @param {string} xpath The XPath query string used to perform the search.
 * @param {string} [searchType=SearchType.Simple] The type of search to perform. Defaults to SearchType.Simple.
 * @return {Promise<import('../types/common-types').RequestResponseArray>}
 */
export async function searchByXpath(xpath, searchType = SearchType.Simple) {
    let pageSize = 500;
    let pageNumber = 1;
    let sort = 'date'; //convertSortField(sort || 'ResourceName.value');

    let order = 'asc';

    let searchFields = {
        pageSize: undefined,
        pageNumber: undefined,
        sort: undefined,
        order: undefined,
        type: undefined,
    };
    searchFields.pageSize = pageSize;
    searchFields.pageNumber = pageNumber;
    searchFields.sort = sort;
    searchFields.order = order;
    searchFields.type = searchType;

    let url =
        process.env.EIDR_API_PROXY_URI +
        `/query?pageSize=${Math.min(pageSize, 500)}&pageNumber=${pageNumber || 1}&sort=${sort || 'title'}&order=${order || 'asc'}&type=${searchType}`;

    let options = {
        method: 'POST',
        headers: {
            'Content-Type': 'text/plain',
            Authorization: await getAuthorization(),
        },
        body: xpath,
    };

    try {

        const response = await fetch(url, options);
        const text = await response.text();
        const responseData = JSON.parse(text);
        delete options.headers.Authorization;
        options.url = url;

        return {
            response: responseData,
            request: options
        };
    } catch (error) {
        console.log('Something Went Wrong', error);
        delete options.headers.Authorization;
        options.url = url;
        return {response: {error: error.message}, request: options};
    }
}

/**
 * Searches for metadata using a provided ShortDOI.
 *
 * @param {string} id - The ShortDOI identifier to search for.
 * @param {string} [searchType=SearchType.Simple] - The type of search to perform. Defaults to a simple search.
 * @return {Promise<import('../types/common-types').RequestResponse>}
 */
export async function searchByShortDoi(id, searchType = SearchType.Simple) {
    let xpath = `ASCII(/FullMetadata/BaseObjectData/AlternateID "${id}")`;

    let url =
        process.env.EIDR_API_PROXY_URI +
        `/query?pageSize=1&sort=date&order=asc&type=${searchType}`;
    let options = {
        method: 'POST',
        headers: {
            'Content-Type': 'text/plain',
            Authorization: await getAuthorization(),
        },
        body: xpath,
    };

    try {
        const response = await fetch(url, options);

        if (response.status > 399) {
            return {
                response: {
                    error: {
                        message: response.statusText
                    }
                },
                request: {
                    url: url,
                    body: xpath,
                    headers: {'Content-Type': 'text/plain'},
                }
            }
        }

        const text = await response.text();
        let responseData = JSON.parse(text);
        if (responseData?.results.length > 0) {
            responseData = responseData.results[0];
        }

        return {
            response: responseData,
            request: {
                url: url,
                body: xpath,
                headers: {'Content-Type': 'text/plain'},
            },
        };
    } catch (error) {
        console.log('Something Went Wrong', error);
        return {
            response: {
                error: error.message
            },
            request: {
                url: url,
                body: xpath,
                headers: {'Content-Type': 'text/plain'},
            }
        }
    }
}

/**
 * Generate a default search query based on the search input
 * @param {string} searchInput - the text (title, id, etc) from a search field
 * @return {SearchPayloadLimited}
 */
export const generateSearchQuery = (searchInput) => {
    const countryOfOrigin = [];
    const originalLanguages = [];
    const referentType = []
    return {
        ...baseQuery,
        resourceName: {
            resourceName: searchInput,
            alternateResourceNameAddition: true,
        },
        alternateId: searchInput,
        filters: {
            rootTitleRecords: true,
            series: true,
            seasons: true,
            episodes: true,
            compilations: true,
        },
        countryOfOrigin,
        originalLanguages,
        referentType
    };
};

/**
 * Sorts the data by sort field and options. Used in Search Results
 * @param {{results:[]}} data
 * @param {string} sortField
 * @param {string} order
 * @return {IContentRecord[]}
 */
export const sortBy = (
    data,
    sortField = 'title',
    order = 'asc',
) => {
    let results = [...data.results];
    let key = sortFieldMap(sortField);
    return results.sort((a, b) => {
        const valueA = get(a, key);
        const valueB = get(b, key);

        if (order === 'asc') {
            return valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
        } else {
            return valueA < valueB ? 1 : valueA > valueB ? -1 : 0;
        }
    });
};

/**
 * A map of value to go from search filters to the actual data values
 * The proxy search query value differ from the returned data values
 * @param {string} sortField
 * @return {string}
 */
export const sortFieldMap = (sortField) => {
    switch (sortField) {
        case 'title':
            return 'ResourceName.value';
        case 'date':
            return 'ReleaseDate';
        case 'length':
            return 'ApproximateLength';
        case 'coo':
            return 'CountryOfOrigin[0]';
        case 'struct':
            return 'StructuralType';
        case 'reftype':
            return 'ReferentType';
        case 'lang':
            return 'OriginalLanguage[0].value';
    }
};
